import {
  BLOCKS,
  BY_FN,
  BY_OPCODE,
  HAT_FNS,
  MATH_OPS,
  menuLabel,
  menuValue,
} from './catalog.js';

const ID_CHARS = '!#%()*+,-./:;=?@[]^_`{|}~ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

export function uid() {
  let s = '';
  for (let i = 0; i < 20; i++) s += ID_CHARS[(Math.random() * ID_CHARS.length) | 0];
  return s;
}

export function isIdent(name) {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name);
}

function shadowType(type) {
  if (type === 'string' || type === 'broadcast') return 10;
  if (type === 'color') return 9;
  if (type === 'angle') return 8;
  return 4;
}

export function parse(text) {
  const rawLines = text.replace(/\t/g, '    ').split(/\r?\n/);
  const rows = [];
  for (let i = 0; i < rawLines.length; i++) {
    const raw = rawLines[i];
    const hash = raw.indexOf('#');
    const noComment = hash >= 0 ? raw.slice(0, hash) : raw;
    if (!noComment.trim()) continue;
    const indent = noComment.match(/^ */)[0].length;
    rows.push({ line: i + 1, indent, text: noComment.trim() });
  }
  const scripts = [];
  let i = 0;
  while (i < rows.length) {
    const row = rows[i];
    if (row.indent !== 0) {
      throw err(row.line, 'Unexpected indent. Scripts start at column 0 with when(): or define.');
    }
    if (row.text.startsWith('when ') || row.text.startsWith('when(') || /^when[ _]/.test(row.text) || row.text.startsWith('define ')) {
      const header = parseHeader(row);
      const [body, next] = parseSuite(rows, i + 1, 0);
      scripts.push({ type: header.type, header, body, line: row.line });
      i = next;
    } else {
      const [body, next] = parseSuite(rows, i, -1);
      scripts.push({ type: 'stack', header: null, body, line: row.line });
      i = next;
    }
  }
  return scripts;
}

function err(line, message) {
  const e = new Error(`Line ${line}: ${message}`);
  e.line = line;
  return e;
}

function parseSuite(rows, start, parentIndent) {
  const body = [];
  let i = start;
  const base = i < rows.length ? rows[i].indent : parentIndent + 4;
  if (i < rows.length && rows[i].indent <= parentIndent) return [body, i];
  while (i < rows.length) {
    const row = rows[i];
    if (row.indent <= parentIndent) break;
    if (row.indent < base) break;
    if (row.indent > base) throw err(row.line, 'Unexpected extra indent.');
    if (row.text === 'else:' || row.text === 'else') {
      break;
    }
    const stmt = parseStatement(row);
    if (stmt.needsSuite) {
      const [inner, next] = parseSuite(rows, i + 1, row.indent);
      stmt.body = inner;
      i = next;
      if (stmt.canElse && i < rows.length && rows[i].indent === row.indent && (rows[i].text === 'else:' || rows[i].text === 'else')) {
        const [ebody, enext] = parseSuite(rows, i + 1, row.indent);
        stmt.orelse = ebody;
        stmt.type = 'ifelse';
        i = enext;
      }
    } else {
      i += 1;
    }
    body.push(stmt);
  }
  return [body, i];
}

function parseHeader(row) {
  let t = row.text;
  if (t.endsWith(':')) t = t.slice(0, -1).trim();
  if (t.startsWith('define ')) {
    const rest = t.slice(7).trim();
    const call = parseCallLine(rest, row.line);
    return { type: 'define', name: call.name, args: call.args, line: row.line };
  }
  if (t.startsWith('when ')) t = t.slice(5).trim();
  else if (t.startsWith('when')) t = t.slice(4).trim();
  if (t === '' || t === '()') {
    return { type: 'hat', fn: 'flag_clicked', args: [], line: row.line };
  }
  // when loudness > 10  / when timer > 10
  const gt = t.match(/^(loudness|timer)\s*>\s*(.+)$/);
  if (gt) {
    return {
      type: 'hat',
      fn: 'greater_than',
      opcode: 'event_whengreaterthan',
      args: [{ type: 'string', value: gt[1] }, parseExpr(gt[2], row.line)],
      line: row.line,
    };
  }
  const call = parseCallLine(t, row.line);
  if (!HAT_FNS.has(call.name) && call.name !== 'greater_than') {
    // still allow when custom() as hat-like
  }
  return { type: 'hat', fn: call.name, args: call.args, line: row.line };
}

function parseStatement(row) {
  let t = row.text;
  const colon = t.endsWith(':');
  if (colon) t = t.slice(0, -1).trim();

  if (t === 'forever' || t === 'forever()') {
    return { type: 'call', fn: 'forever', args: [], needsSuite: true, line: row.line };
  }
  if (t.startsWith('if ') || t === 'if' || t.startsWith('if(')) {
    const condSrc = t.startsWith('if') ? t.slice(2).trim() : '';
    const cond = condSrc.startsWith('(') && condSrc.endsWith(')')
      ? parseExpr(condSrc, row.line)
      : parseExpr(condSrc.replace(/^\(/, '').replace(/\)$/, ''), row.line);
    return { type: 'if', fn: 'if', cond, args: [cond], needsSuite: true, canElse: true, line: row.line };
  }
  if (t.startsWith('repeat_until')) {
    const call = parseCallLine(t, row.line);
    return { type: 'call', fn: 'repeat_until', args: call.args, needsSuite: true, line: row.line };
  }
  if (t.startsWith('repeat')) {
    const call = parseCallLine(t, row.line);
    return { type: 'call', fn: 'repeat', args: call.args, needsSuite: true, line: row.line };
  }
  if (/^[A-Za-z_][A-Za-z0-9_]*\s*(\+|-)?=\s*/.test(t) && !t.includes('(')) {
    const m = t.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*(\+|-)?=\s*(.*)$/);
    if (m[2] === '+') {
      return { type: 'call', fn: 'change', args: [identOrString(m[1]), parseExpr(m[3], row.line)], line: row.line };
    }
    if (m[2] === '-') {
      return {
        type: 'call',
        fn: 'change',
        args: [identOrString(m[1]), { type: 'binop', op: '-', left: { type: 'number', value: 0 }, right: parseExpr(m[3], row.line) }],
        line: row.line,
      };
    }
    return { type: 'call', fn: 'set', args: [identOrString(m[1]), parseExpr(m[3], row.line)], line: row.line };
  }
  const call = parseCallLine(t, row.line);
  return { type: 'call', fn: call.name, args: call.args, line: row.line };
}

function identOrString(name) {
  return { type: 'ident', name };
}

function parseCallLine(src, line) {
  const tokens = tokenize(src, line);
  let p = 0;
  const nameTok = tokens[p++];
  if (!nameTok || nameTok.t !== 'id') throw err(line, `Expected a name, got "${src}"`);
  const name = nameTok.v;
  if (!tokens[p] || tokens[p].t !== '(') {
    if (tokens.length === 1) return { name, args: [] };
    throw err(line, `Expected ( after ${name}`);
  }
  p++;
  const args = [];
  if (tokens[p] && tokens[p].t === ')') return { name, args };
  while (p < tokens.length) {
    const [expr, np] = parseExprTokens(tokens, p, line, 0);
    args.push(expr);
    p = np;
    if (tokens[p] && tokens[p].t === ',') { p++; continue; }
    if (tokens[p] && tokens[p].t === ')') break;
    throw err(line, `Bad arguments in ${name}(...)`);
  }
  return { name, args };
}

export function parseExpr(src, line = 1) {
  const tokens = tokenize(src, line);
  const [expr, p] = parseExprTokens(tokens, 0, line, 0);
  if (p < tokens.length) throw err(line, `Unexpected "${tokens[p].v}" in expression`);
  return expr;
}

function parseExprTokens(tokens, pos, line, minPrec) {
  let [left, p] = parseUnary(tokens, pos, line);
  while (p < tokens.length) {
    const tok = tokens[p];
    const prec = binPrec(tok);
    if (prec == null || prec < minPrec) break;
    p++;
    const [right, np] = parseExprTokens(tokens, p, line, prec + (tok.v === '**' ? 0 : 1));
    left = { type: 'binop', op: tok.v, left, right };
    p = np;
  }
  return [left, p];
}

function parseUnary(tokens, pos, line) {
  const tok = tokens[pos];
  if (!tok) throw err(line, 'Expected expression');
  if (tok.t === 'op' && (tok.v === '-' || tok.v === 'not')) {
    const [expr, p] = parseUnary(tokens, pos + 1, line);
    if (tok.v === 'not') return [{ type: 'unary', op: 'not', expr }, p];
    return [{ type: 'binop', op: '-', left: { type: 'number', value: 0 }, right: expr }, p];
  }
  if (tok.t === '(') {
    const [expr, p] = parseExprTokens(tokens, pos + 1, line, 0);
    if (!tokens[p] || tokens[p].t !== ')') throw err(line, 'Missing )');
    return [expr, p + 1];
  }
  if (tok.t === 'num') return [{ type: 'number', value: Number(tok.v) }, pos + 1];
  if (tok.t === 'str') return [{ type: 'string', value: tok.v }, pos + 1];
  if (tok.t === 'id') {
    if (tokens[pos + 1] && tokens[pos + 1].t === '(') {
      let p = pos + 2;
      const args = [];
      if (tokens[p] && tokens[p].t === ')') return [{ type: 'call', fn: tok.v, args }, p + 1];
      while (p < tokens.length) {
        const [expr, np] = parseExprTokens(tokens, p, line, 0);
        args.push(expr);
        p = np;
        if (tokens[p] && tokens[p].t === ',') { p++; continue; }
        if (tokens[p] && tokens[p].t === ')') return [{ type: 'call', fn: tok.v, args }, p + 1];
        throw err(line, `Bad call ${tok.v}(...)`);
      }
    }
    return [{ type: 'ident', name: tok.v }, pos + 1];
  }
  throw err(line, `Unexpected token ${tok.v}`);
}

function binPrec(tok) {
  if (!tok) return null;
  if (tok.t !== 'op' && tok.t !== 'id') return null;
  const v = tok.v;
  if (v === 'or') return 1;
  if (v === 'and') return 2;
  if (v === '==' || v === '!=' || v === '<' || v === '>' || v === '<=' || v === '>=') return 3;
  if (v === '+' || v === '-') return 4;
  if (v === '*' || v === '/' || v === '%') return 5;
  return null;
}

function tokenize(src, line) {
  const out = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (/\s/.test(c)) { i++; continue; }
    if (c === '"' || c === "'") {
      const q = c;
      let s = '';
      i++;
      while (i < src.length && src[i] !== q) {
        if (src[i] === '\\' && i + 1 < src.length) { s += src[i + 1]; i += 2; continue; }
        s += src[i++];
      }
      if (i >= src.length) throw err(line, 'Unterminated string');
      i++;
      out.push({ t: 'str', v: s });
      continue;
    }
    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(src[i + 1] || ''))) {
      let s = '';
      while (i < src.length && /[0-9.]/.test(src[i])) s += src[i++];
      out.push({ t: 'num', v: s });
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      let s = '';
      while (i < src.length && /[A-Za-z0-9_]/.test(src[i])) s += src[i++];
      out.push({ t: s === 'and' || s === 'or' || s === 'not' ? 'op' : 'id', v: s });
      continue;
    }
    const two = src.slice(i, i + 2);
    if (['==', '!=', '<=', '>='].includes(two)) {
      out.push({ t: 'op', v: two });
      i += 2;
      continue;
    }
    if ('+-*/%<>'.includes(c)) { out.push({ t: 'op', v: c }); i++; continue; }
    if (c === '(' || c === ')' || c === ',') { out.push({ t: c, v: c }); i++; continue; }
    throw err(line, `Bad character "${c}"`);
  }
  return out;
}

function literalValue(node) {
  if (!node) return '';
  if (node.type === 'number') return String(node.value);
  if (node.type === 'string') return node.value;
  if (node.type === 'ident') return node.name;
  return null;
}

function nameOf(node) {
  if (!node) return null;
  if (node.type === 'ident') return node.name;
  if (node.type === 'string') return node.value;
  if (node.type === 'call' && (node.fn === 'var' || node.fn === 'list') && node.args?.[0]) {
    return literalValue(node.args[0]);
  }
  return literalValue(node);
}

function scriptKey(script, index) {
  if (script.header?.type === 'define' || script.type === 'define') {
    return `define:${script.header?.name || 'block'}`;
  }
  if (script.header) {
    const fn = script.header.fn || 'hat';
    const extra = (script.header.args || []).map((a) => literalValue(a) ?? '').join(',');
    return `when:${fn}:${extra}`;
  }
  return `stack:${index}`;
}

export function compileScripts(scripts, ctx) {
  const blocks = {};
  const usedExt = new Set();
  let y = 40;
  const seenKeys = new Map();
  for (let si = 0; si < scripts.length; si++) {
    const script = scripts[si];
    let key = scriptKey(script, si);
    const n = seenKeys.get(key) || 0;
    seenKeys.set(key, n + 1);
    if (n) key = `${key}#${n}`;
    const topId = ctx.with(key, () => {
      if (script.type === 'define' || script.header?.type === 'define') {
        return compileDefine(script, blocks, ctx);
      }
      if (script.header) {
        const hatId = compileHat(script.header, blocks, ctx, usedExt);
        const bodyId = compileStack(script.body, blocks, ctx, usedExt);
        if (bodyId) {
          blocks[hatId].next = bodyId;
          blocks[bodyId].parent = hatId;
        }
        return hatId;
      }
      return compileStack(script.body, blocks, ctx, usedExt);
    });
    if (topId) {
      blocks[topId].topLevel = true;
      blocks[topId].x = 40;
      blocks[topId].y = y;
      y += 160;
    }
  }
  return { blocks, extensions: [...usedExt] };
}

function compileHat(header, blocks, ctx, usedExt) {
  const id = ctx.id(header.opcode || header.fn || 'hat');
  if (header.opcode === 'event_whengreaterthan') {
    const menu = String(literalValue(header.args[0]) || 'LOUDNESS').toUpperCase();
    const value = compileInput(header.args[1], blocks, ctx, usedExt, id, 'number');
    blocks[id] = {
      opcode: 'event_whengreaterthan',
      next: null,
      parent: null,
      inputs: { VALUE: value.input },
      fields: { WHENGREATERTHANMENU: [menu === 'TIMER' ? 'TIMER' : 'LOUDNESS', null] },
      shadow: false,
      topLevel: true,
    };
    return id;
  }
  const defs = BY_FN.get(header.fn) || [];
  const def = defs.find((d) => d.kind === 'hat') || defs[0];
  if (!def) throw err(header.line, `Unknown hat when ${header.fn}()`);
  if (def.extension) usedExt.add(def.extension);
  blocks[id] = {
    opcode: def.opcode,
    next: null,
    parent: null,
    inputs: {},
    fields: {},
    shadow: false,
    topLevel: true,
  };
  applyDef(def, header.args, id, blocks, ctx, usedExt);
  return id;
}

function compileDefine(script, blocks, ctx) {
  const defId = ctx.id('procedures_definition');
  const protoId = ctx.with('proto', () => ctx.id('procedures_prototype'));
  const header = script.header;
  const argIds = header.args.map((a, i) => ctx.with(`arg:${nameOf(a) || i}`, () => ctx.id('procarg')));
  const proccode = header.name + header.args.map(() => ' %s').join('');
  const argumentids = JSON.stringify(argIds);
  const argumentnames = JSON.stringify(header.args.map((a, i) => {
    if (a.type === 'ident') return a.name;
    return literalValue(a) || `arg${i + 1}`;
  }));
  const argumentdefaults = JSON.stringify(header.args.map(() => ''));
  blocks[protoId] = {
    opcode: 'procedures_prototype',
    next: null,
    parent: defId,
    inputs: {},
    fields: {},
    shadow: true,
    topLevel: false,
    mutation: {
      tagName: 'mutation',
      children: [],
      proccode,
      argumentids,
      argumentnames,
      argumentdefaults,
      warp: 'false',
    },
  };
  blocks[defId] = {
    opcode: 'procedures_definition',
    next: null,
    parent: null,
    inputs: { custom_block: [1, protoId] },
    fields: {},
    shadow: false,
    topLevel: true,
  };
  ctx.procedures[header.name] = { proccode, argumentids, argumentnames, warp: false };
  const bodyId = ctx.with('body', () => compileStack(script.body, blocks, ctx, new Set()));
  if (bodyId) {
    blocks[defId].next = bodyId;
    blocks[bodyId].parent = defId;
  }
  return defId;
}

function compileStack(stmts, blocks, ctx, usedExt) {
  let first = null;
  let prev = null;
  let i = 0;
  for (const stmt of stmts || []) {
    const id = ctx.with(`$${i++}`, () => compileStmt(stmt, blocks, ctx, usedExt));
    if (!id) continue;
    if (!first) first = id;
    if (prev) {
      blocks[prev].next = id;
      blocks[id].parent = prev;
    }
    prev = id;
  }
  return first;
}

function compileStmt(stmt, blocks, ctx, usedExt) {
  if (stmt.type === 'if' || stmt.type === 'ifelse') {
    const opcode = stmt.type === 'ifelse' || stmt.orelse ? 'control_if_else' : 'control_if';
    const id = ctx.id(opcode);
    const cond = ctx.with('cond', () => compileInput(stmt.cond, blocks, ctx, usedExt, id, 'boolean'));
    blocks[id] = {
      opcode,
      next: null,
      parent: null,
      inputs: { CONDITION: cond.input },
      fields: {},
      shadow: false,
      topLevel: false,
    };
    const bodyId = ctx.with('then', () => compileStack(stmt.body, blocks, ctx, usedExt));
    if (bodyId) {
      blocks[id].inputs.SUBSTACK = [2, bodyId];
      blocks[bodyId].parent = id;
    }
    if (opcode === 'control_if_else') {
      const elseId = ctx.with('else', () => compileStack(stmt.orelse || [], blocks, ctx, usedExt));
      if (elseId) {
        blocks[id].inputs.SUBSTACK2 = [2, elseId];
        blocks[elseId].parent = id;
      }
    }
    return id;
  }
  return compileCallStmt(stmt, blocks, ctx, usedExt);
}

function compileCallStmt(stmt, blocks, ctx, usedExt) {
  if (stmt.fn === 'pass') return null;
  const defs = BY_FN.get(stmt.fn) || [];
  const def = defs.find((d) => d.kind === 'stack' || d.kind === 'c' || d.kind === 'cap' || d.kind === 'cs') || defs[0];
  if (def) {
    const id = ctx.id(def.opcode);
    if (def.extension) usedExt.add(def.extension);
    blocks[id] = {
      opcode: def.opcode,
      next: null,
      parent: null,
      inputs: {},
      fields: {},
      shadow: false,
      topLevel: false,
    };
    applyDef(def, stmt.args || [], id, blocks, ctx, usedExt);
    if (def.substack) {
      const bodyId = ctx.with('do', () => compileStack(stmt.body || [], blocks, ctx, usedExt));
      if (bodyId) {
        blocks[id].inputs[def.substack] = [2, bodyId];
        blocks[bodyId].parent = id;
      }
    }
    return id;
  }
  if (ctx.procedures[stmt.fn]) {
    const proc = ctx.procedures[stmt.fn];
    const id = ctx.id('procedures_call');
    const argIds = JSON.parse(proc.argumentids);
    const inputs = {};
    stmt.args.forEach((arg, i) => {
      const compiled = compileInput(arg, blocks, ctx, usedExt, id, 'string');
      inputs[`arg${argIds[i]}`] ? null : null;
      inputs['input' + i] = compiled.input;
    });
    // Scratch uses argument ids as input names
    const named = {};
    argIds.forEach((aid, i) => {
      if (stmt.args[i]) {
        named[aid] = ctx.with(`callarg:${i}`, () => compileInput(stmt.args[i], blocks, ctx, usedExt, id, 'string')).input;
      }
    });
    blocks[id] = {
      opcode: 'procedures_call',
      next: null,
      parent: null,
      inputs: named,
      fields: {},
      shadow: false,
      topLevel: false,
      mutation: {
        tagName: 'mutation',
        children: [],
        proccode: proc.proccode,
        argumentids: proc.argumentids,
        warp: proc.warp ? 'true' : 'false',
      },
    };
    return id;
  }
  throw err(stmt.line || 1, `Unknown block ${stmt.fn}()`);
}

function applyDef(def, args, id, blocks, ctx, usedExt) {
  if (def.fieldFixed) {
    for (const [k, v] of Object.entries(def.fieldFixed)) {
      blocks[id].fields[k] = [v, null];
    }
  }
  if (def.fields) {
    for (const f of def.fields) {
      const node = args[f.fromArg ?? 0];
      const val = literalValue(node) ?? '';
      if (f.broadcast) {
        const bId = ctx.ensureBroadcast(val);
        blocks[id].fields[f.key] = [val, bId];
      } else {
        blocks[id].fields[f.key] = [String(val), null];
      }
    }
  }
  if (def.varField) {
    const node = args[def.varField.fromArg ?? 0];
    const name = nameOf(node);
    const rec = ctx.findVar(name);
    if (!rec) throw err(1, `Unknown variable "${name}". Create it in the Variables sidebar.`);
    blocks[id].fields[def.varField.key] = [rec.name, rec.id];
  }
  if (def.listField) {
    const node = args[def.listField.fromArg ?? 0];
    const name = nameOf(node);
    const rec = ctx.findList(name);
    if (!rec) throw err(1, `Unknown list "${name}". Create it in the Variables sidebar.`);
    blocks[id].fields[def.listField.key] = [rec.name, rec.id];
  }
  if (def.inputs) {
    for (let i = 0; i < def.inputs.length; i++) {
      const spec = def.inputs[i];
      const argIndex = spec.fromArg ?? i;
      const node = args[argIndex];
      if (!node && spec.type === 'boolean') continue;
      if (!node) continue;
      const compiled = ctx.with(`in:${spec.key}`, () => compileInput(node, blocks, ctx, usedExt, id, spec.type));
      blocks[id].inputs[spec.key] = compiled.input;
    }
  }
}

function compileInput(node, blocks, ctx, usedExt, parentId, type) {
  if (!node) {
    return { input: type === 'boolean' ? [1, null] : [1, null] };
  }
  if (node.type === 'ident') {
    const v = ctx.findVar(node.name);
    if (v) return { input: [3, [12, v.name, v.id], shadowFor(type, '')] };
    const l = ctx.findList(node.name);
    if (l) return { input: [3, [13, l.name, l.id], shadowFor(type, '')] };
    if (ctx.procArgs && ctx.procArgs.has(node.name)) {
      const id = ctx.id('argument_reporter_string_number');
      blocks[id] = {
        opcode: 'argument_reporter_string_number',
        next: null,
        parent: parentId,
        inputs: {},
        fields: { VALUE: [node.name, null] },
        shadow: false,
        topLevel: false,
      };
      return { input: [3, id, shadowFor(type, '')] };
    }
    // treat as string/menu literal
    return wrapLiteral(String(node.name), type, parentId, blocks, ctx);
  }
  if (node.type === 'number' || node.type === 'string') {
    return wrapLiteral(node.type === 'number' ? String(node.value) : node.value, type, parentId, blocks, ctx);
  }
  if (node.type === 'unary' && node.op === 'not') {
    const id = ctx.id('operator_not');
    const inner = ctx.with('not', () => compileInput(node.expr, blocks, ctx, usedExt, id, 'boolean'));
    blocks[id] = {
      opcode: 'operator_not',
      next: null,
      parent: parentId,
      inputs: { OPERAND: inner.input },
      fields: {},
      shadow: false,
      topLevel: false,
    };
    return { input: type === 'boolean' ? [2, id] : [3, id, shadowFor(type, '')] };
  }
  if (node.type === 'binop') {
    if (node.op === '!=') {
      const eq = { type: 'binop', op: '==', left: node.left, right: node.right };
      return compileInput({ type: 'unary', op: 'not', expr: eq }, blocks, ctx, usedExt, parentId, type);
    }
    if (node.op === '>=') {
      return compileInput({
        type: 'binop',
        op: 'or',
        left: { type: 'binop', op: '>', left: node.left, right: node.right },
        right: { type: 'binop', op: '==', left: node.left, right: node.right },
      }, blocks, ctx, usedExt, parentId, type);
    }
    if (node.op === '<=') {
      return compileInput({
        type: 'binop',
        op: 'or',
        left: { type: 'binop', op: '<', left: node.left, right: node.right },
        right: { type: 'binop', op: '==', left: node.left, right: node.right },
      }, blocks, ctx, usedExt, parentId, type);
    }
    const opcode = MATH_OPS[node.op];
    if (!opcode) throw new Error(`Unsupported operator ${node.op}`);
    const id = ctx.id(opcode);
    const aType = node.op === 'and' || node.op === 'or' ? 'boolean' : 'number';
    const left = ctx.with('L', () => compileInput(node.left, blocks, ctx, usedExt, id, aType));
    const right = ctx.with('R', () => compileInput(node.right, blocks, ctx, usedExt, id, aType));
    const inputs = opcode === 'operator_and' || opcode === 'operator_or'
      ? { OPERAND1: left.input, OPERAND2: right.input }
      : opcode === 'operator_gt' || opcode === 'operator_lt' || opcode === 'operator_equals'
        ? { OPERAND1: left.input, OPERAND2: right.input }
        : { NUM1: left.input, NUM2: right.input };
    blocks[id] = {
      opcode,
      next: null,
      parent: parentId,
      inputs,
      fields: {},
      shadow: false,
      topLevel: false,
    };
    const boolish = ['operator_gt', 'operator_lt', 'operator_equals', 'operator_and', 'operator_or', 'operator_not'].includes(opcode);
    return { input: boolish && type === 'boolean' ? [2, id] : [3, id, shadowFor(type, '')] };
  }
  if (node.type === 'call') {
    return compileReporterCall(node, blocks, ctx, usedExt, parentId, type);
  }
  throw new Error('Cannot compile expression');
}

function compileReporterCall(node, blocks, ctx, usedExt, parentId, type) {
  if (node.fn === 'var') {
    const rec = ctx.findVar(nameOf(node.args[0]));
    if (!rec) throw new Error(`Unknown variable "${nameOf(node.args[0])}"`);
    return { input: [3, [12, rec.name, rec.id], shadowFor(type, '')] };
  }
  if (node.fn === 'list') {
    const rec = ctx.findList(nameOf(node.args[0]));
    if (!rec) throw new Error(`Unknown list "${nameOf(node.args[0])}"`);
    return { input: [3, [13, rec.name, rec.id], shadowFor(type, '')] };
  }
  const defs = BY_FN.get(node.fn) || [];
  const def = defs.find((d) => d.kind === 'reporter' || d.kind === 'boolean')
    || (node.fn === 'key_pressed' ? defs.find((d) => d.opcode === 'sensing_keypressed') : null)
    || defs[0];
  if (!def) {
    const v = ctx.findVar(node.fn);
    if (v) return { input: [3, [12, v.name, v.id], shadowFor(type, '')] };
    throw new Error(`Unknown reporter ${node.fn}()`);
  }
  if (def.extension) usedExt.add(def.extension);
  const id = ctx.id(def.opcode);
  blocks[id] = {
    opcode: def.opcode,
    next: null,
    parent: parentId,
    inputs: {},
    fields: {},
    shadow: false,
    topLevel: false,
  };
  applyDef(def, node.args || [], id, blocks, ctx, usedExt);
  const isBool = def.kind === 'boolean';
  return { input: isBool && type === 'boolean' ? [2, id] : [3, id, shadowFor(type, '')] };
}

function shadowFor(type, value) {
  if (type === 'boolean') return null;
  return [shadowType(type), value == null ? '' : String(value)];
}

function wrapLiteral(value, type, parentId, blocks, ctx) {
  if (type === 'boolean') {
    return { input: [1, null] };
  }
  if (type === 'goto' || type === 'glide' || type === 'towards' || type === 'touching' || type === 'distance' || type === 'clone' || type === 'costume' || type === 'backdrop' || type === 'sound' || type === 'key' || type === 'of' || type === 'penparam' || type === 'broadcast') {
    return menuInput(type, value, parentId, blocks, ctx);
  }
  return { input: [1, [shadowType(type), String(value)]] };
}

const MENU_OP = {
  goto: ['motion_goto_menu', 'TO'],
  glide: ['motion_glideto_menu', 'TO'],
  towards: ['motion_pointtowards_menu', 'TOWARDS'],
  touching: ['sensing_touchingobjectmenu', 'TOUCHINGOBJECTMENU'],
  distance: ['sensing_distancetomenu', 'DISTANCETOMENU'],
  clone: ['control_create_clone_of_menu', 'CLONE_OPTION'],
  costume: ['looks_costume', 'COSTUME'],
  backdrop: ['looks_backdrops', 'BACKDROP'],
  sound: ['sound_sounds_menu', 'SOUND_MENU'],
  key: ['sensing_keyoptions', 'KEY_OPTION'],
  of: ['sensing_of_object_menu', 'OBJECT'],
  penparam: ['pen_menu_colorParam', 'colorParam'],
};

function menuInput(type, value, parentId, blocks, ctx) {
  if (type === 'broadcast') {
    return { input: [1, [11, String(value), null]] };
  }
  const spec = MENU_OP[type];
  if (!spec) return { input: [1, [10, String(value)]] };
  const id = ctx.id(spec[0]);
  const fieldVal = menuValue(value);
  blocks[id] = {
    opcode: spec[0],
    next: null,
    parent: parentId,
    inputs: {},
    fields: { [spec[1]]: [fieldVal, null] },
    shadow: true,
    topLevel: false,
  };
  return { input: [1, id] };
}

export function makeCompileCtx(project, target) {
  const procedures = {};
  const oldMap = target._idMap || {};
  const used = new Set();
  const newMap = {};
  const path = [];
  const idAt = (key, opcode) => {
    const prev = oldMap[key];
    let id;
    if (prev && prev.opcode === opcode && prev.id && !used.has(prev.id)) id = prev.id;
    else id = uid();
    used.add(id);
    newMap[key] = { id, opcode };
    return id;
  };
  return {
    procedures,
    procArgs: null,
    _path: path,
    newMap,
    with(seg, fn) {
      path.push(seg);
      try { return fn(); } finally { path.pop(); }
    },
    id(opcode) {
      return idAt(`${path.join('/')}@${opcode}`, opcode);
    },
    finish() {
      target._idMap = newMap;
    },
    ensureBroadcast(name) {
      const stage = project.targets.find((t) => t.isStage) || project.targets[0];
      stage.broadcasts = stage.broadcasts || {};
      for (const [bid, n] of Object.entries(stage.broadcasts)) {
        if (n === name) return bid;
      }
      const id = idAt(`broadcast:${name}`, 'broadcast');
      stage.broadcasts[id] = name;
      return id;
    },
    findVar(name) {
      if (name == null) return null;
      const n = String(name);
      const search = [target, ...project.targets.filter((t) => t.isStage)];
      for (const t of search) {
        for (const [id, rec] of Object.entries(t.variables || {})) {
          if (rec[0] === n || id === n) return { id, name: rec[0], value: rec[1], target: t };
        }
      }
      return null;
    },
    findList(name) {
      if (name == null) return null;
      const n = String(name);
      const search = [target, ...project.targets.filter((t) => t.isStage)];
      for (const t of search) {
        for (const [id, rec] of Object.entries(t.lists || {})) {
          if (rec[0] === n || id === n) return { id, name: rec[0], value: rec[1], target: t };
        }
      }
      return null;
    },
  };
}

export function compileTarget(project, target) {
  const text = target.scriptText || '';
  const scripts = parse(text);
  const ctx = makeCompileCtx(project, target);
  const { blocks, extensions } = compileScripts(scripts, ctx);
  ctx.finish();
  target.blocks = blocks;
  return extensions;
}

export function compileProject(project) {
  const ext = new Set(project.extensions || []);
  for (const t of project.targets) {
    for (const e of compileTarget(project, t)) ext.add(e);
  }
  project.extensions = [...ext];
  return project;
}

/* ---------------- decompiler ---------------- */

export function decompileTarget(target, project) {
  const blocks = target.blocks || {};
  const tops = Object.entries(blocks)
    .filter(([, b]) => b.topLevel && !b.shadow)
    .sort((a, b) => (a[1].y || 0) - (b[1].y || 0) || (a[1].x || 0) - (b[1].x || 0));
  const chunks = [];
  for (const [id] of tops) {
    chunks.push(decompileStack(id, blocks, target, project, 0));
  }
  target.scriptText = chunks.filter(Boolean).join('\n\n');
  if (!target.scriptText.trim()) {
    target.scriptText = 'when flag_clicked():\n    # add blocks from the palette\n';
  }
  return target.scriptText;
}

function decompileStack(id, blocks, target, project, indent) {
  const pad = '    '.repeat(indent);
  const lines = [];
  let cur = id;
  let first = true;
  let bodyIndent = indent;
  while (cur) {
    const b = blocks[cur];
    if (!b) break;
    if (b.opcode === 'procedures_definition') {
      lines.push(decompileDefine(b, blocks, target, project, indent));
      break;
    }
    if (first && (b.opcode.startsWith('event_when') || b.opcode === 'control_start_as_clone')) {
      lines.push(pad + decompileHat(b, blocks, target, project));
      bodyIndent = indent + 1;
      cur = b.next;
      first = false;
      continue;
    }
    first = false;
    lines.push(decompileBlock(b, blocks, target, project, bodyIndent));
    cur = b.next;
  }
  return lines.filter((x) => x !== '').join('\n');
}

function decompileHat(b, blocks, target, project) {
  if (b.opcode === 'event_whenflagclicked') return 'when flag_clicked():';
  if (b.opcode === 'event_whenkeypressed') return `when key_pressed(${quote(b.fields.KEY_OPTION?.[0])}):`;
  if (b.opcode === 'event_whenthisspriteclicked') return 'when this_sprite_clicked():';
  if (b.opcode === 'event_whenstageclicked') return 'when stage_clicked():';
  if (b.opcode === 'event_whenbackdropswitchesto') return `when backdrop_switches_to(${quote(b.fields.BACKDROP?.[0])}):`;
  if (b.opcode === 'event_whenbroadcastreceived') return `when received(${quote(b.fields.BROADCAST_OPTION?.[0])}):`;
  if (b.opcode === 'control_start_as_clone') return 'when start_as_clone():';
  if (b.opcode === 'event_whengreaterthan') {
    const which = (b.fields.WHENGREATERTHANMENU?.[0] || 'LOUDNESS').toLowerCase();
    return `when ${which} > ${decompileInput(b.inputs.VALUE, blocks, target, project)}:`;
  }
  return `when unknown():`;
}

function decompileDefine(b, blocks, target, project, indent) {
  const protoId = flattenInput(b.inputs.custom_block);
  const proto = blocks[protoId];
  let name = 'custom';
  let args = [];
  if (proto?.mutation) {
    const names = JSON.parse(proto.mutation.argumentnames || '[]');
    const proccode = proto.mutation.proccode || 'custom';
    name = proccode.split(' %')[0].replace(/ /g, '_');
    args = names;
  }
  const pad = '    '.repeat(indent);
  const header = `${pad}define ${name}(${args.join(', ')}):`;
  const body = b.next ? decompileStack(b.next, blocks, target, project, indent + 1) : `${pad}    pass`;
  return `${header}\n${body}`;
}

function decompileBlock(b, blocks, target, project, indent) {
  const pad = '    '.repeat(indent);
  if (b.opcode === 'control_if' || b.opcode === 'control_if_else') {
    const cond = decompileInput(b.inputs.CONDITION, blocks, target, project) || 'False';
    let out = `${pad}if ${stripParens(cond)}:\n`;
    out += b.inputs.SUBSTACK ? decompileStack(flattenInput(b.inputs.SUBSTACK), blocks, target, project, indent + 1) : `${pad}    pass`;
    if (b.opcode === 'control_if_else') {
      out += `\n${pad}else:\n`;
      out += b.inputs.SUBSTACK2 ? decompileStack(flattenInput(b.inputs.SUBSTACK2), blocks, target, project, indent + 1) : `${pad}    pass`;
    }
    return out;
  }
  if (b.opcode === 'control_repeat') {
    const n = decompileInput(b.inputs.TIMES, blocks, target, project);
    let out = `${pad}repeat(${n}):\n`;
    out += b.inputs.SUBSTACK ? decompileStack(flattenInput(b.inputs.SUBSTACK), blocks, target, project, indent + 1) : `${pad}    pass`;
    return out;
  }
  if (b.opcode === 'control_forever') {
    let out = `${pad}forever:\n`;
    out += b.inputs.SUBSTACK ? decompileStack(flattenInput(b.inputs.SUBSTACK), blocks, target, project, indent + 1) : `${pad}    pass`;
    return out;
  }
  if (b.opcode === 'control_repeat_until') {
    const cond = decompileInput(b.inputs.CONDITION, blocks, target, project);
    let out = `${pad}repeat_until(${cond}):\n`;
    out += b.inputs.SUBSTACK ? decompileStack(flattenInput(b.inputs.SUBSTACK), blocks, target, project, indent + 1) : `${pad}    pass`;
    return out;
  }
  if (b.opcode === 'procedures_call') {
    const proccode = b.mutation?.proccode || 'custom';
    const name = proccode.split(' %')[0].replace(/ /g, '_');
    const argIds = JSON.parse(b.mutation?.argumentids || '[]');
    const args = argIds.map((aid) => decompileInput(b.inputs[aid], blocks, target, project));
    return `${pad}${name}(${args.join(', ')})`;
  }
  const expr = decompileOpcode(b, blocks, target, project, true);
  return pad + expr;
}

function decompileOpcode(b, blocks, target, project, asStmt) {
  if (b.opcode === 'operator_add') return bin(b, '+', blocks, target, project);
  if (b.opcode === 'operator_subtract') return bin(b, '-', blocks, target, project);
  if (b.opcode === 'operator_multiply') return bin(b, '*', blocks, target, project);
  if (b.opcode === 'operator_divide') return bin(b, '/', blocks, target, project);
  if (b.opcode === 'operator_mod') return bin(b, '%', blocks, target, project);
  if (b.opcode === 'operator_gt') return bin(b, '>', blocks, target, project);
  if (b.opcode === 'operator_lt') return bin(b, '<', blocks, target, project);
  if (b.opcode === 'operator_equals') return bin(b, '==', blocks, target, project);
  if (b.opcode === 'operator_and') return bin(b, 'and', blocks, target, project);
  if (b.opcode === 'operator_or') return bin(b, 'or', blocks, target, project);
  if (b.opcode === 'operator_not') return `not ${decompileInput(b.inputs.OPERAND, blocks, target, project)}`;
  if (b.opcode === 'operator_join') {
    return `join(${decompileInput(b.inputs.STRING1, blocks, target, project)}, ${decompileInput(b.inputs.STRING2, blocks, target, project)})`;
  }
  if (b.opcode === 'data_variable') {
    return varName(b.fields.VARIABLE?.[0]);
  }
  if (b.opcode === 'argument_reporter_string_number' || b.opcode === 'argument_reporter_boolean') {
    const n = b.fields.VALUE?.[0] || 'arg';
    return isIdent(n) ? n : quote(n);
  }
  const defs = BY_OPCODE.get(b.opcode) || [];
  let def = defs[0];
  if (b.opcode === 'looks_costumenumbername') {
    def = defs.find((d) => d.fieldFixed?.NUMBER_NAME === b.fields.NUMBER_NAME?.[0]) || def;
  }
  if (b.opcode === 'looks_backdropnumbername') {
    def = defs.find((d) => d.fieldFixed?.NUMBER_NAME === b.fields.NUMBER_NAME?.[0]) || def;
  }
  if (b.opcode === 'operator_mathop') {
    const op = b.fields.OPERATOR?.[0];
    def = defs.find((d) => d.fieldFixed?.OPERATOR === op) || def;
  }
  if (b.opcode === 'sensing_keypressed') {
    def = defs.find((d) => d.opcode === 'sensing_keypressed') || def;
  }
  if (!def) return `${b.opcode}()`;
  const args = [];
  const consumed = new Set();
  if (def.fields) {
    for (const f of def.fields) {
      args[f.fromArg ?? args.length] = quote(b.fields[f.key]?.[0]);
      consumed.add(f.fromArg ?? 0);
    }
  }
  if (def.varField) {
    args[def.varField.fromArg ?? 0] = varName(b.fields[def.varField.key]?.[0]);
  }
  if (def.listField) {
    args[def.listField.fromArg ?? 0] = listName(b.fields[def.listField.key]?.[0]);
  }
  if (def.inputs) {
    for (let i = 0; i < def.inputs.length; i++) {
      const spec = def.inputs[i];
      const idx = spec.fromArg ?? i;
      args[idx] = decompileInput(b.inputs[spec.key], blocks, target, project, spec.type);
    }
  }
  const cleaned = [];
  for (let i = 0; i < args.length; i++) if (args[i] !== undefined) cleaned.push(args[i]);
  if (def.kind === 'c' || def.kind === 'cs') {
    return `${def.fn}(${cleaned.join(', ')})`;
  }
  return `${def.fn}(${cleaned.join(', ')})`;
}

function bin(b, op, blocks, target, project) {
  const a = decompileInput(b.inputs.NUM1 || b.inputs.OPERAND1, blocks, target, project);
  const c = decompileInput(b.inputs.NUM2 || b.inputs.OPERAND2, blocks, target, project);
  if (op === '-' && (a === '0' || a === 0)) return `-${c}`;
  return `(${a} ${op} ${c})`;
}

function decompileInput(input, blocks, target, project, type) {
  if (!input) return type === 'boolean' ? 'False' : '""';
  const inner = input[1];
  if (inner == null) return type === 'boolean' ? 'False' : '""';
  if (Array.isArray(inner)) {
    const kind = inner[0];
    if (kind === 12) return varName(inner[1]);
    if (kind === 13) return listName(inner[1]);
    if (kind === 11) return quote(inner[1]);
    const val = inner[1];
    if (kind === 4 || kind === 5 || kind === 6 || kind === 7 || kind === 8) {
      return String(val);
    }
    if (kind === 9) return quote(val);
    if (val !== '' && Number.isFinite(Number(val)) && String(Number(val)) === String(val).trim()) {
      return String(val).trim();
    }
    return quote(val);
  }
  if (typeof inner === 'string' && blocks[inner]) {
    const b = blocks[inner];
    if (b.shadow && b.fields) {
      const field = Object.values(b.fields)[0];
      return quote(menuLabel(field?.[0]));
    }
    return decompileOpcode(b, blocks, target, project, false);
  }
  return String(inner);
}

function flattenInput(input) {
  if (!input) return null;
  if (typeof input[1] === 'string') return input[1];
  return null;
}

function quote(v) {
  if (v == null) return '""';
  return JSON.stringify(String(v));
}

function varName(name) {
  if (name && isIdent(name)) return name;
  return `var(${quote(name || '')})`;
}

function listName(name) {
  if (name && isIdent(name)) return name;
  return `list(${quote(name || '')})`;
}

function stripParens(s) {
  if (s.startsWith('(') && s.endsWith(')')) return s.slice(1, -1);
  return s;
}

export function highlight(text) {
  const fnClass = (fn) => {
    const defs = BY_FN.get(fn);
    return defs?.[0]?.cat || 'motion';
  };
  return text.split('\n').map((line) => {
    if (line.trim().startsWith('#')) return `<span class="tok-comment">${esc(line)}</span>`;
    let html = '';
    const re = /("(?:\\.|[^"])*"|'(?:\\.|[^'])*')|(#.*$)|(\b(?:if|else|forever|not|and|or|when|define|repeat|repeat_until|wait_until)\b)|(\b[A-Za-z_][A-Za-z0-9_]*\b)|(-?\b\d+(?:\.\d+)?\b)|(.)/g;
    let m;
    while ((m = re.exec(line))) {
      if (m[1]) html += `<span class="tok-string">${esc(m[1])}</span>`;
      else if (m[2]) html += `<span class="tok-comment">${esc(m[2])}</span>`;
      else if (m[3]) html += `<span class="tok-kw">${esc(m[3])}</span>`;
      else if (m[4]) {
        const cat = fnClass(m[4]);
        const known = BY_FN.has(m[4]);
        html += known ? `<span class="tok-${cat}">${esc(m[4])}</span>` : esc(m[4]);
      } else if (m[5]) html += `<span class="tok-number">${esc(m[5])}</span>`;
      else html += esc(m[6]);
    }
    return html || ' ';
  }).join('\n');
}

function esc(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export { BLOCKS };
